import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import AppDateInput from '@/app/admin/components/AppDateInput';

const isEmptyDate = (value) => value === undefined || value === null || value === ''

const withTodayReceptionDates = (paymentMethod, values = {}) => {
  const champs = paymentMethod?.champs_saisie
  if (!Array.isArray(champs)) return values
  const today = dayjs().format('YYYY-MM-DD')
  let changed = false
  const next = { ...values }
  champs.forEach((field) => {
    if (
      field?.type === 'date' &&
      (field.name === 'date_reception' || field.name === 'date_emission') &&
      isEmptyDate(next[field.name])
    ) {
      next[field.name] = today
      changed = true
    }
  })
  return changed ? next : values
}

/**
 * Component to render dynamic form fields based on payment method's champs_saisie configuration
 * @param {Object} props
 * @param {Object} props.paymentMethod - The payment method object with champs_saisie
 * @param {Object} props.fieldValues - Current field values
 * @param {Function} props.onFieldChange - Callback when field values change
 * @param {boolean} props.disabled - Whether fields should be disabled
 * @param {string} props.amount - The payment amount for this method
 * @param {boolean} props.requireAmount - Whether to require amount to be set before showing fields (default: true)
 */
const PaymentMethodFields = ({
  paymentMethod,
  fieldValues = {},
  onFieldChange,
  disabled = false,
  amount = 0,
  requireAmount = true,
  variant = 'default',
  onAmountChange,
  amountMax,
  amountReadOnly = false,
  displayCurrency,
  displayAmount = (val) => Number(Number(val).toFixed(2)),
}) => {
  const { t, i18n } = useTranslation();
  const [localFieldValues, setLocalFieldValues] = useState(() =>
    withTodayReceptionDates(paymentMethod, fieldValues)
  );
  const currentLanguage = i18n.language || 'fr';

  useEffect(() => {
    const next = withTodayReceptionDates(paymentMethod, fieldValues)
    setLocalFieldValues(next)
    const filledReception =
      isEmptyDate(fieldValues?.date_reception) && !isEmptyDate(next.date_reception)
    const filledEmission =
      isEmptyDate(fieldValues?.date_emission) && !isEmptyDate(next.date_emission)
    if (filledReception || filledEmission) {
      queueMicrotask(() => onFieldChange?.(next))
    }
  }, [fieldValues, paymentMethod]);

  // Mirror row "Montant" into the details `montant` field when the payment amount changes
  useEffect(() => {
    if (!paymentMethod?.champs_saisie?.some(f => f.name === 'montant')) return;
    const n = Number(amount);
    if (!Number.isFinite(n) || n < 0) return;
    setLocalFieldValues(prev => {
      const cur = prev.montant;
      if (cur != null && cur !== '' && Math.abs(Number(cur) - n) < 0.01) {
        return prev;
      }
      const next = { ...prev, montant: n };
      queueMicrotask(() => onFieldChange?.(next));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only follow external payment amount
  }, [amount, paymentMethod]);

  // Validate field value based on field type
  const validateField = (field, value) => {
    if (!value && value !== 0) return null; // No validation for empty values

    switch (field.type) {
      case 'string':
        if (field.name === 'email_paypal' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(value)
            ? null
            : t('moteurReservation.paymentFields.validation.invalid_email', {
                defaultValue: 'Invalid email format',
              });
        }
        if (field.name === 'iban' && value) {
          const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;
          return ibanRegex.test(value.replace(/\s/g, ''))
            ? null
            : t('moteurReservation.paymentFields.validation.invalid_iban', {
                defaultValue: 'Invalid IBAN format',
              });
        }
        if (field.name === 'numero_carte' && value) {
          const cardRegex = /^[0-9]{13,19}$/;
          return cardRegex.test(value.replace(/\s/g, ''))
            ? null
            : t('moteurReservation.paymentFields.validation.invalid_card_number', {
                defaultValue: 'Invalid card number',
              });
        }
        if (field.name === 'ccv' && value) {
          const ccvRegex = /^[0-9]{3,4}$/;
          return ccvRegex.test(value)
            ? null
            : t('moteurReservation.paymentFields.validation.invalid_ccv', {
                defaultValue: 'Invalid CCV',
              });
        }
        if (field.name === 'numero_telephone' && value) {
          const phoneRegex = /^[+]?[0-9\s\-\(\)]{8,15}$/;
          return phoneRegex.test(value)
            ? null
            : t('moteurReservation.paymentFields.validation.invalid_phone', {
                defaultValue: 'Invalid phone number',
              });
        }
        return null;

      case 'number':
        if (field.name === 'montant' && value !== '' && value != null) {
          const vNum = Number(value);
          const aNum = Number(amount);
          if (
            Number.isFinite(vNum) &&
            Number.isFinite(aNum) &&
            Math.abs(vNum - aNum) > 0.009
          ) {
            return t('moteurReservation.paymentFields.validation.amount_mismatch', {
              defaultValue: 'Amount must match payment amount',
              amount: amount,
            });
          }
        }
        if (value < 0) {
          return t('moteurReservation.paymentFields.validation.negative_number', {
            defaultValue: 'Cannot be negative',
          });
        }
        return null;

      case 'date':
        if (value) {
          const inputDate = dayjs(value);
          const today = dayjs();
          if (field.name === 'date_expiration' && inputDate.isBefore(today)) {
            return t('moteurReservation.paymentFields.validation.expired_date', {
              defaultValue: 'Date has expired',
            });
          }
          if (field.name === 'date_emission' && inputDate.isAfter(today)) {
            return t('moteurReservation.paymentFields.validation.future_date', {
              defaultValue: 'Date cannot be in the future',
            });
          }
        }
        return null;

      default:
        return null;
    }
  };

  // Handle field value change
  const handleFieldChange = (fieldName, value) => {
    const newValues = {
      ...localFieldValues,
      [fieldName]: value,
    };
    setLocalFieldValues(newValues);
    onFieldChange?.(newValues);
  };

  // Render different field types based on the field configuration
  const renderField = field => {
    const { name, type, options } = field;
    const currentValue = localFieldValues[name] || '';
    const validationError = validateField(field, currentValue);
    const hasError = validationError !== null;

    const tamarra = variant === 'tamarra'
    const baseInputClasses = tamarra
      ? 'tamarra-pay-input'
      : `w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
          hasError
            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
            : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
        }`;

    const fieldElement = (() => {
      switch (type) {
        case 'string':
          return (
            <input
              type='text'
              value={currentValue}
              onChange={e => handleFieldChange(name, e.target.value)}
              disabled={disabled}
              className={baseInputClasses}
              placeholder={t(`moteurReservation.paymentFields.${name}.placeholder`, {
                defaultValue: `Enter ${getFieldLabel(field)}`,
              })}
            />
          );

        case 'number':
          return (
            <input
              type='number'
              value={currentValue}
              onChange={e => handleFieldChange(name, parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className={baseInputClasses}
              placeholder={t(`moteurReservation.paymentFields.${name}.placeholder`, {
                defaultValue: `Enter ${getFieldLabel(field)}`,
              })}
            />
          );

        case 'date':
          return (
            <AppDateInput
              value={currentValue ? dayjs(currentValue).format('YYYY-MM-DD') : ''}
              onChange={(nextValue) =>
                handleFieldChange(name, nextValue || '')
              }
              disabled={disabled}
              compact
              triggerClassName={baseInputClasses}
            />
          );

        case 'select':
          return (
            <select
              value={currentValue}
              onChange={e => handleFieldChange(name, e.target.value)}
              disabled={disabled}
              className={baseInputClasses}
            >
              <option value=''>
                {t(`moteurReservation.paymentFields.${name}.placeholder`, {
                  defaultValue: `Select ${getFieldLabel(name)}`,
                })}
              </option>
              {options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );

        default:
          return (
            <input
              type='text'
              value={currentValue}
              onChange={e => handleFieldChange(name, e.target.value)}
              disabled={disabled}
              className={baseInputClasses}
              placeholder={t(`moteurReservation.paymentFields.${name}.placeholder`, {
                defaultValue: `Enter ${getFieldLabel(field)}`,
              })}
            />
          );
      }
    })();

    return (
      <div className={tamarra ? 'tamarra-pf' : ''}>
        {tamarra ? <span>{getFieldLabel(field)}</span> : null}
        {fieldElement}
        {hasError ? (
          tamarra ? (
            <small className="tamarra-ferr" role="alert">
              {validationError}
            </small>
          ) : (
            <div className="mt-1 text-sm text-red-600 flex items-center">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {validationError}
            </div>
          )
        ) : null}
      </div>
    );
  };

  // Get field label with translation from field.translations or fallback
  const getFieldLabel = field => {
    // Handle string input (for backward compatibility)
    if (typeof field === 'string') {
      const labelMap = {
        numero_cheque: currentLanguage === 'fr' ? 'Numéro de chèque' : 'Check Number',
        banque: currentLanguage === 'fr' ? 'Banque' : 'Bank',
        date_emission: currentLanguage === 'fr' ? 'Date réception' : 'Issue Date',
        date_reception: currentLanguage === 'fr' ? 'Date réception' : 'Reception Date',
        montant: currentLanguage === 'fr' ? 'Montant' : 'Amount',
        date_echeance: currentLanguage === 'fr' ? 'Date échéance' : 'Due Date',
      };
      return labelMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    const fieldName = field?.name;
    if (!fieldName) {
      return '';
    }

    // Priority 1: If field has translations object, use it based on current language
    if (field?.translations && typeof field.translations === 'object') {
      const lang = currentLanguage === 'fr' ? 'fr' : 'en';
      const translation =
        field.translations[lang] || field.translations.fr || field.translations.en;
      if (translation && translation.trim() && !translation.startsWith('paymentFields.')) {
        return translation;
      }
    }

    // Priority 2: Direct label mapping with French/English defaults
    const labelMap = {
      numero_cheque: currentLanguage === 'fr' ? 'Numéro de chèque' : 'Check Number',
      banque: currentLanguage === 'fr' ? 'Banque' : 'Bank',
      date_emission: currentLanguage === 'fr' ? 'Date réception' : 'Issue Date',
      iban: 'IBAN',
      date_virement: currentLanguage === 'fr' ? 'Date du virement' : 'Transfer Date',
      code_MTCN: 'Code MTCN',
      expediteur: currentLanguage === 'fr' ? 'Expéditeur' : 'Sender',
      numero_carte: currentLanguage === 'fr' ? 'Numéro de carte' : 'Card Number',
      type_carte: currentLanguage === 'fr' ? 'Type de carte' : 'Card Type',
      date_expiration: currentLanguage === 'fr' ? "Date d'expiration" : 'Expiry Date',
      ccv: 'CCV',
      nom_porteur: currentLanguage === 'fr' ? 'Nom du porteur' : 'Cardholder Name',
      numero_telephone: currentLanguage === 'fr' ? 'Numéro de téléphone' : 'Phone Number',
      email_paypal: currentLanguage === 'fr' ? 'Email PayPal' : 'PayPal Email',
      mandat_reference: currentLanguage === 'fr' ? 'Référence du mandat' : 'Mandate Reference',
      numero_traite: currentLanguage === 'fr' ? 'Numéro de traite' : 'Bill of Exchange Number',
      banque_emettrice: currentLanguage === 'fr' ? 'Banque émettrice' : 'Issuing Bank',
      date_echeance: currentLanguage === 'fr' ? 'Date échéance' : 'Due Date',
      montant: currentLanguage === 'fr' ? 'Montant' : 'Amount',
      tireur: currentLanguage === 'fr' ? 'Tireur' : 'Drawer',
      tire: currentLanguage === 'fr' ? 'Tiré' : 'Drawee',
      date_reception: currentLanguage === 'fr' ? 'Date réception' : 'Reception Date',
      numero_compte: currentLanguage === 'fr' ? 'Numéro de compte' : 'Account Number',
      nom_debiteur: currentLanguage === 'fr' ? 'Nom du débiteur' : 'Debtor Name',
      nom_hotel: currentLanguage === 'fr' ? "Nom de l'hôtel" : 'Hotel Name',
      date_paiement: currentLanguage === 'fr' ? 'Date de paiement' : 'Payment Date',
    };

    // Return mapped label or format field name as fallback
    if (labelMap[fieldName]) {
      return labelMap[fieldName];
    }

    // Last resort: format field name nicely
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Don't render if no payment method or no champs_saisie
  if (
    !paymentMethod ||
    !paymentMethod.champs_saisie ||
    !Array.isArray(paymentMethod.champs_saisie) ||
    paymentMethod.champs_saisie.length === 0
  ) {
    return null;
  }

  // Don't render if amount is 0 or not set (only if requireAmount is true)
  if (requireAmount && (!amount || amount <= 0)) {
    return null;
  }

  const dateFieldNames = ['date_reception', 'date_emission']
  const dateFields = paymentMethod.champs_saisie.filter((field) =>
    dateFieldNames.includes(field?.name),
  )
  const otherFields =
    variant === 'tamarra'
      ? paymentMethod.champs_saisie.filter(
          (field) => field?.name !== 'montant' && !dateFieldNames.includes(field?.name),
        )
      : paymentMethod.champs_saisie
  const showMontantBesideDate = variant === 'tamarra' && dateFields.length > 0

  if (variant === 'tamarra') {
    if (!dateFields.length && !otherFields.length && !showMontantBesideDate) return null
    return (
      <div className="tamarra-pay-extra">
        <span className="tamarra-label">
          {t('tamarraBooking.payment.extraTitle', { defaultValue: 'Infos du règlement' })}
        </span>
        {showMontantBesideDate ? (
          <div className="tamarra-pay-extra-row">
            <label className="tamarra-pf">
              <span>
                {t('moteurReservation.automaticPaymentMethods.amount_label', {
                  defaultValue: 'Montant',
                })}
              </span>
              <input
                type="number"
                className="tamarra-pay-input"
                min={0}
                max={amountMax != null ? amountMax : undefined}
                step="0.01"
                value={amount === '' || amount == null ? '' : amount}
                disabled={disabled || amountReadOnly}
                readOnly={amountReadOnly}
                aria-readonly={amountReadOnly ? 'true' : undefined}
                onChange={(e) => {
                  if (amountReadOnly || !onAmountChange) return
                  const raw = e.target.value
                  onAmountChange(raw === '' ? 0 : parseFloat(raw) || 0)
                }}
              />
            </label>
            {dateFields.map((field, index) => (
              <div key={`${field.name}-${index}`}>{renderField(field)}</div>
            ))}
          </div>
        ) : null}
        {!showMontantBesideDate && dateFields.length ? (
          <div className="tamarra-pay-extra-g">
            {dateFields.map((field, index) => (
              <div key={`${field.name}-${index}`}>{renderField(field)}</div>
            ))}
          </div>
        ) : null}
        {otherFields.length ? (
          <div className="tamarra-pay-extra-g">
            {otherFields.map((field, index) => (
              <div key={`${field.name}-${index}`}>{renderField(field)}</div>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className='mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg w-full'>
      <div className='flex items-start mb-3'>
        <div className='flex-shrink-0'>
          <svg className='h-5 w-5 text-blue-400' viewBox='0 0 20 20' fill='currentColor'>
            <path
              fillRule='evenodd'
              d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
              clipRule='evenodd'
            />
          </svg>
        </div>
        <div className='ml-3'>
          <h4 className='text-sm font-medium text-blue-800'>
            {t('moteurReservation.paymentFields.title', { defaultValue: 'Payment Details' })}
          </h4>
          <p className='text-sm text-blue-700 mt-1'>
            {t('moteurReservation.paymentFields.description', {
              defaultValue: 'Please provide the following information for this payment method',
            })}
          </p>
        </div>
      </div>

      <div className='flex flex-wrap gap-4 w-full'>
        {paymentMethod.champs_saisie.map((field, index) => (
          <div
            key={index}
            className='space-y-1'
            style={{ maxWidth: '200px', minWidth: '150px', flex: '0 1 auto' }}
          >
            <label className='block text-sm font-medium text-gray-700'>
              {getFieldLabel(field)}
              {field.type === 'number' && field.name === 'montant' && (
                <span className='text-gray-500 ml-1'>
                  ({t('moteurReservation.paymentFields.amount_note', { defaultValue: 'Amount: ' })}
                  {displayAmount(amount)}
                  {displayCurrency ? ` ${displayCurrency}` : ''})
                </span>
              )}
            </label>
            {renderField(field)}
          </div>
        ))}
      </div>

      {/* Show a summary of filled fields */}
      {Object.keys(localFieldValues).length > 0 && (
        <div className='mt-4 p-3 bg-white border border-blue-200 rounded-lg w-full'>
          <h5 className='text-sm font-medium text-gray-700 mb-2'>
            {t('moteurReservation.paymentFields.summary', {
              defaultValue: 'Payment Information Summary',
            })}
          </h5>
          <div className='space-y-1'>
            {Object.entries(localFieldValues).map(([key, value]) => {
              const field = paymentMethod.champs_saisie?.find(f => f.name === key);
              return (
                <div key={key} className='flex justify-between text-sm'>
                  <span className='text-gray-600'>{getFieldLabel(field || key)}:</span>
                  <span className='font-medium text-gray-900'>
                    {value ||
                      t('moteurReservation.paymentFields.not_provided', {
                        defaultValue: 'Not provided',
                      })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodFields;
